import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Clock, CheckCircle2, User } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import useReveal from "../hooks/useReveal";
import usePageMeta from "../hooks/usePageMeta";
import { getArticleBySlug, ARTICLES } from "../data/articles";

const EYEBROW =
    "font-display font-bold tracking-[0.26em] uppercase text-srm-gold text-[12px] lg:text-[13px]";
const GOLD_RULE = "block h-[3px] w-16 bg-srm-gold";

/* Renders "[anchor](/path)" markers in article copy as internal links. */
function withLinks(text) {
    return text.split(/(\[[^\]]+\]\(\/[^)]*\))/g).map((part, i) => {
        const m = part.match(/^\[([^\]]+)\]\((\/[^)]*)\)$/);
        if (!m) return part;
        return (
            <Link
                key={i}
                to={m[2]}
                className="text-srm-navy font-medium underline underline-offset-4 decoration-srm-gold/60 hover:text-srm-gold transition-colors"
            >
                {m[1]}
            </Link>
        );
    });
}

/* === Hero (compact) ============================================== */
function ArticleHero({ article }) {
    const ref = useReveal();
    return (
        <section ref={ref} data-testid="article-hero" className="bg-white">
            <div className="container-srm pt-12 pb-10 lg:pt-16 lg:pb-12">
                <div className="reveal max-w-[820px] mx-auto">
                    <nav aria-label="Breadcrumb">
                        <Link
                            to="/resources"
                            data-testid="article-back"
                            className="inline-flex items-center gap-2 font-display font-semibold tracking-[0.2em] uppercase text-srm-navy/55 hover:text-srm-navy text-[11px] transition-colors"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
                            Back to Resources
                        </Link>
                    </nav>

                    <div className="mt-7 flex items-center gap-3 flex-wrap">
                        <span className={EYEBROW}>{article.category}</span>
                        <span aria-hidden className="text-srm-navy/25">·</span>
                        <span className="inline-flex items-center gap-1.5 font-display font-semibold tracking-[0.18em] uppercase text-srm-navy/55 text-[11px]">
                            <Clock className="h-3 w-3" strokeWidth={1.8} />
                            {article.readTime}
                        </span>
                        <span aria-hidden className="text-srm-navy/25">·</span>
                        <span className="font-display font-semibold tracking-[0.18em] uppercase text-srm-navy/55 text-[11px]">
                            {article.label}
                        </span>
                    </div>

                    {article.tags && article.tags.length > 0 && (
                        <div
                            data-testid="article-tags"
                            className="mt-4 flex flex-wrap items-center gap-2"
                        >
                            {article.tags.map((t) => (
                                <span
                                    key={t}
                                    data-testid={`article-tag-${t.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="inline-flex items-center rounded-full bg-srm-navy/[0.04] ring-1 ring-srm-navy/10 text-srm-navy/75 font-display font-semibold tracking-[0.14em] uppercase text-[10px] px-3 py-1"
                                >
                                    {t}
                                </span>
                            ))}
                        </div>
                    )}

                    <span aria-hidden className={`mt-5 ${GOLD_RULE}`} />

                    <h1
                        data-testid="article-headline"
                        className="mt-6 font-display font-bold text-[2rem] sm:text-[2.4rem] lg:text-[2.85rem] tracking-[-0.024em] leading-[1.1] text-srm-navy text-balance"
                    >
                        {article.title}
                    </h1>

                    <p className="mt-6 text-[15.5px] lg:text-[17px] text-srm-body leading-[1.8]">
                        {article.intro}
                    </p>

                    {/* Article metadata bar */}
                    <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-[12px] text-srm-navy/60 font-display font-semibold tracking-[0.16em] uppercase">
                        <span className="inline-flex flex-col gap-0.5 items-start">
                            <span className="inline-flex items-center gap-2">
                                <User className="h-3.5 w-3.5 text-srm-gold" strokeWidth={1.8} />
                                {article.author || "SRM Editorial Team"}
                            </span>
                            {article.authorRole && (
                                <span className="pl-[22px] text-srm-navy/50 tracking-[0.14em] normal-case font-normal text-[11.5px]">
                                    {article.authorRole}
                                </span>
                            )}
                        </span>
                        {article.publishedDate && (
                            <>
                                <span aria-hidden className="text-srm-navy/20">·</span>
                                <span
                                    data-testid="article-published-date"
                                    className="inline-flex items-center gap-2 tracking-[0.14em] normal-case font-normal text-[11.5px] text-srm-navy/60"
                                >
                                    Published {article.publishedDate}
                                </span>
                            </>
                        )}
                        <span aria-hidden className="text-srm-navy/20">·</span>
                        <span className="inline-flex items-center gap-2">
                            <Clock className="h-3.5 w-3.5 text-srm-gold" strokeWidth={1.8} />
                            {article.readTime}
                        </span>
                    </div>

                    {article.heroImage && (
                        <div
                            data-testid="article-hero-image"
                            className="mt-10 overflow-hidden rounded-2xl ring-1 ring-srm-line shadow-[0_10px_30px_rgba(15,40,86,0.08)]"
                        >
                            <img
                                src={article.heroImage}
                                alt={article.heroAlt || article.title}
                                loading="eager"
                                decoding="async"
                                className="w-full h-56 sm:h-72 lg:h-[420px] object-cover"
                            />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

/* === Prev / Next article navigation =============================== */
function ArticleNav({ currentSlug }) {
    const index = ARTICLES.findIndex((a) => a.slug === currentSlug);
    const prev = index > 0 ? ARTICLES[index - 1] : null;
    const next = index >= 0 && index < ARTICLES.length - 1 ? ARTICLES[index + 1] : null;
    if (!prev && !next) return null;
    return (
        <div
            data-testid="article-nav"
            className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5 print:hidden"
        >
            {prev ? (
                <Link
                    to={`/resources/${prev.slug}`}
                    data-testid="article-nav-prev"
                    className="group relative flex flex-col gap-2 overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-5 lg:p-6 shadow-[0_6px_24px_rgba(15,40,86,0.07)] transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_22px_48px_rgba(15,40,86,0.12)]"
                >
                    <span aria-hidden className="pointer-events-none absolute left-0 top-0 h-[3px] w-12 bg-srm-gold transition-all duration-500 group-hover:w-full" />
                    <div className="inline-flex items-center gap-2 font-display font-semibold tracking-[0.2em] uppercase text-srm-navy/55 text-[10px]">
                        <ArrowLeft className="h-3 w-3" strokeWidth={2} />
                        Previous Article
                    </div>
                    <div className="font-display font-bold text-srm-navy text-[1rem] lg:text-[1.1rem] tracking-[-0.008em] leading-[1.3] group-hover:text-srm-gold transition-colors duration-300">
                        {prev.title}
                    </div>
                </Link>
            ) : (
                <div aria-hidden />
            )}
            {next ? (
                <Link
                    to={`/resources/${next.slug}`}
                    data-testid="article-nav-next"
                    className="group relative flex flex-col items-end gap-2 text-right overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-5 lg:p-6 shadow-[0_6px_24px_rgba(15,40,86,0.07)] transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-[0_22px_48px_rgba(15,40,86,0.12)]"
                >
                    <span aria-hidden className="pointer-events-none absolute right-0 top-0 h-[3px] w-12 bg-srm-gold transition-all duration-500 group-hover:w-full" />
                    <div className="inline-flex items-center gap-2 font-display font-semibold tracking-[0.2em] uppercase text-srm-navy/55 text-[10px]">
                        Next Article
                        <ArrowRight className="h-3 w-3" strokeWidth={2} />
                    </div>
                    <div className="font-display font-bold text-srm-navy text-[1rem] lg:text-[1.1rem] tracking-[-0.008em] leading-[1.3] group-hover:text-srm-gold transition-colors duration-300">
                        {next.title}
                    </div>
                </Link>
            ) : (
                <div aria-hidden />
            )}
        </div>
    );
}

/* === Prose — paragraphs, plus optional { h2 } and { list } items === */
function ArticleProse({ items, prefix }) {
    return (
        <div className="space-y-5 text-[15.5px] lg:text-[16.5px] text-srm-body leading-[1.85]">
            {items.map((item, i) => {
                if (item.h2) {
                    return (
                        <h2
                            key={item.h2}
                            data-testid={`${prefix}-heading-${i}`}
                            className="!mt-10 font-display font-bold text-srm-navy text-[1.3rem] sm:text-[1.45rem] lg:text-[1.6rem] tracking-[-0.014em] leading-[1.25] text-balance"
                        >
                            {item.h2}
                        </h2>
                    );
                }
                if (item.list) {
                    return (
                        <ul
                            key={`list-${i}`}
                            data-testid={`${prefix}-list-${i}`}
                            className="list-disc pl-6 space-y-1.5 marker:text-srm-gold"
                        >
                            {item.list.map((li) => (
                                <li key={li}>{li}</li>
                            ))}
                        </ul>
                    );
                }
                return (
                    <p key={item.slice(0, 32)} data-testid={`${prefix}-${i}`}>
                        {withLinks(item)}
                    </p>
                );
            })}
        </div>
    );
}

/* === Body — main prose, then 2-col Key Considerations + Questions  */
function ArticleBody({ article }) {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="article-body"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-14 lg:py-20">
                <div className="reveal max-w-[820px] mx-auto">
                    {/* Main prose */}
                    <ArticleProse items={article.body} prefix="article-para" />

                    {/* Article-specific disclaimer (insurance article) */}
                    {article.disclaimer && (
                        <div
                            data-testid="article-disclaimer"
                            className="mt-10 relative rounded-2xl bg-white ring-1 ring-srm-navy/15 p-5 lg:p-6"
                        >
                            <div className="font-display font-bold tracking-[0.2em] uppercase text-srm-navy/60 text-[10px]">
                                Disclaimer
                            </div>
                            <p className="mt-3 text-[13px] lg:text-[13.5px] text-srm-body leading-[1.7] italic">
                                {article.disclaimer}
                            </p>
                        </div>
                    )}

                    {/* Two-column: Key Considerations | Practical Questions */}
                    <div className={`mt-14 grid grid-cols-1 gap-7 lg:gap-10 ${article.keyPoints?.length ? "lg:grid-cols-2" : ""}`}>
                        {/* Key Considerations */}
                        {article.keyPoints?.length > 0 && (
                        <div className="relative overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-7 lg:p-8 shadow-[0_6px_24px_rgba(15,40,86,0.07)]">
                            <span
                                aria-hidden
                                className="pointer-events-none absolute left-0 top-0 h-[3px] w-12 bg-srm-gold"
                            />
                            <div className={EYEBROW}>Key Considerations</div>
                            <ul className="mt-6 space-y-4">
                                {article.keyPoints.map((point) => (
                                    <li
                                        key={point.slice(0, 32)}
                                        data-testid={`article-keypoint-${article.keyPoints.indexOf(point)}`}
                                        className="flex items-start gap-3.5"
                                    >
                                        <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-srm-gold/10 ring-1 ring-srm-gold/40 text-srm-gold">
                                            <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} />
                                        </span>
                                        <span className="text-[14.5px] lg:text-[15px] text-srm-navy leading-[1.65]">
                                            {point}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        )}

                        {/* Practical Questions */}
                        <div className="relative overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-7 lg:p-8 shadow-[0_6px_24px_rgba(15,40,86,0.07)]">
                            <span
                                aria-hidden
                                className="pointer-events-none absolute left-0 top-0 h-[3px] w-12 bg-srm-gold"
                            />
                            <div className={EYEBROW}>
                                {article.questions.length === 7
                                    ? "Seven Questions Worth Asking"
                                    : "Questions to Ask"}
                            </div>
                            <ol className="mt-6 space-y-4 list-none">
                                {article.questions.map((q, qi) => (
                                    <li
                                        key={q.slice(0, 32)}
                                        data-testid={`article-question-${qi}`}
                                        className="flex items-start gap-3.5"
                                    >
                                        <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-srm-gold/10 ring-1 ring-srm-gold/40 text-srm-gold font-display font-bold text-[11px] tracking-[0.02em]">
                                            {String(qi + 1).padStart(2, "0")}
                                        </span>
                                        <span className="text-[14.5px] lg:text-[15px] text-srm-navy leading-[1.65]">
                                            {q}
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>

                    {article.afterQuestions && (
                        <div className="mt-12">
                            <ArticleProse items={article.afterQuestions} prefix="article-after-para" />
                        </div>
                    )}

                    {/* What this means for committees — compact callout */}
                    {article.committeeImpact && (
                    <div className="mt-12 relative rounded-2xl bg-white ring-1 ring-srm-gold/40 p-7 lg:p-9 shadow-[0_6px_24px_rgba(15,40,86,0.07)]">
                        <span
                            aria-hidden
                            className="pointer-events-none absolute left-0 top-0 h-[3px] w-full bg-srm-gold"
                        />
                        <div className={EYEBROW}>
                            {article.slug === "before-the-first-storm-is-your-strata-roof-ready"
                                ? "After a Storm: Safety and Evidence First"
                                : "What This Means For Committees"}
                        </div>
                        <p className="mt-5 text-[15.5px] lg:text-[17px] text-srm-navy leading-[1.8]">
                            {article.committeeImpact}
                        </p>
                    </div>
                    )}

                    {/* Conclusion */}
                    <p className="mt-10 text-[16px] lg:text-[17.5px] text-srm-navy leading-[1.8] font-display font-medium tracking-[-0.005em] italic">
                        {article.closing}
                    </p>

                    {/* References — appears only where the article provides them */}
                    {article.references && article.references.length > 0 && (
                        <div
                            data-testid="article-references"
                            className="mt-12 relative rounded-2xl bg-white ring-1 ring-srm-line p-6 lg:p-8"
                        >
                            <div className={EYEBROW}>References</div>
                            <ul className="mt-5 space-y-2.5 text-[13.5px] lg:text-[14px] text-srm-body leading-[1.65]">
                                {article.references.map((r, ri) => (
                                    <li
                                        key={r.href}
                                        data-testid={`article-reference-${ri}`}
                                        className="break-words"
                                    >
                                        <a
                                            href={r.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-srm-navy underline-offset-4 hover:underline hover:text-srm-gold transition-colors"
                                        >
                                            {r.label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Per-article final CTA — restrained, appears only where the article defines one */}
                    {article.cta && (
                        <div
                            data-testid="article-cta"
                            className="mt-12 relative rounded-2xl bg-white ring-1 ring-srm-gold/40 p-8 lg:p-10 shadow-[0_6px_24px_rgba(15,40,86,0.07)]"
                        >
                            <span
                                aria-hidden
                                className="pointer-events-none absolute left-0 top-0 h-[3px] w-full bg-srm-gold"
                            />
                            <h2
                                data-testid="article-cta-heading"
                                className="font-display font-bold text-srm-navy text-[1.35rem] sm:text-[1.5rem] lg:text-[1.7rem] tracking-[-0.014em] leading-[1.2]"
                            >
                                {article.cta.heading}
                            </h2>
                            <p className="mt-4 text-[14.5px] lg:text-[15.5px] text-srm-body leading-[1.8]">
                                {withLinks(article.cta.body)}
                            </p>
                            <div className="mt-6">
                                <Link
                                    to={article.cta.buttonHref}
                                    data-testid="article-cta-button"
                                    className="group inline-flex items-center justify-center gap-2 rounded-sm bg-srm-gold text-white text-[11.5px] font-semibold tracking-[0.2em] uppercase px-8 h-[48px] border border-srm-gold hover:bg-srm-goldDark hover:border-srm-goldDark transition-all duration-300"
                                >
                                    {article.cta.buttonLabel}
                                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                                </Link>
                            </div>
                        </div>
                    )}

                    {/* Prev / Next article navigation */}
                    <ArticleNav currentSlug={article.slug} />
                </div>
            </div>
        </section>
    );
}

/* === Related Articles (compact) =================================== */
/* Preferred related slugs are declared per-article so the "storm season"
   piece surfaces the three approved companions, in the approved order.
   Any article without a preference falls back to the first three siblings
   from the ARTICLES catalogue. */
const PREFERRED_RELATED = {
    "before-the-first-storm-is-your-strata-roof-ready": [
        "why-insurance-claims-get-denied",
        "strata-roof-safety-regular-assessments",
        "pros-and-cons-of-roof-guardianship-program",
    ],
};

function RelatedArticles({ currentSlug }) {
    const ref = useReveal();
    const preferred = PREFERRED_RELATED[currentSlug];
    const bySlug = new Map(ARTICLES.map((a) => [a.slug, a]));
    const related = preferred
        ? preferred.map((s) => bySlug.get(s)).filter(Boolean).slice(0, 3)
        : ARTICLES.filter((a) => a.slug !== currentSlug).slice(0, 3);
    return (
        <section
            ref={ref}
            data-testid="article-related"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-14 lg:py-20">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <div className={EYEBROW}>Related Insights</div>
                    <span aria-hidden className={`mx-auto mt-4 ${GOLD_RULE}`} />
                    <h2 className="mt-6 font-display font-bold text-[1.7rem] sm:text-[2rem] lg:text-[2.2rem] tracking-[-0.02em] leading-[1.12] text-srm-navy text-balance">
                        Continue reading.
                    </h2>
                </div>

                <div className="mt-10 lg:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
                    {related.map((a, i) => (
                        <Link
                            key={a.slug}
                            to={`/resources/${a.slug}`}
                            data-testid={`related-${i}`}
                            className="reveal group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-7 lg:p-8 shadow-[0_6px_24px_rgba(15,40,86,0.07)] transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_28px_60px_rgba(15,40,86,0.14)]"
                            style={{ transitionDelay: `${i * 80}ms` }}
                        >
                            <span
                                aria-hidden
                                className="pointer-events-none absolute left-0 top-0 h-[3px] w-16 bg-srm-gold transition-all duration-500 group-hover:w-full"
                            />
                            <div className="flex items-center justify-between gap-3">
                                <div className="font-display font-bold tracking-[0.22em] uppercase text-srm-gold text-[10.5px]">
                                    {a.category}
                                </div>
                                <div className="inline-flex items-center gap-1.5 text-srm-navy/55 font-display font-semibold tracking-[0.18em] uppercase text-[9.5px]">
                                    <Clock className="h-3 w-3" strokeWidth={1.8} />
                                    {a.readTime}
                                </div>
                            </div>
                            <span aria-hidden className="mt-4 block h-[2px] w-10 bg-srm-gold" />
                            <h3 className="mt-4 font-display font-bold text-srm-navy text-[1.15rem] lg:text-[1.25rem] tracking-[-0.008em] leading-[1.25] group-hover:text-srm-gold transition-colors duration-300">
                                {a.title}
                            </h3>
                            <div className="mt-auto pt-6 inline-flex items-center gap-2 font-display font-semibold tracking-[0.2em] uppercase text-srm-navy text-[11px] group-hover:text-srm-gold transition-colors duration-300">
                                Read Article
                                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* === Final CTA — navy ============================================= */
function ArticleCTA() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="article-cta"
            className="relative overflow-hidden"
            style={{ backgroundColor: "#163E7A" }}
        >
            <div
                aria-hidden
                className="pointer-events-none absolute -top-32 -right-32 h-[380px] w-[380px] rounded-full"
                style={{ background: "radial-gradient(circle, rgba(201,165,92,0.10) 0%, rgba(201,165,92,0) 70%)" }}
            />
            <div
                aria-hidden
                className="pointer-events-none absolute -bottom-40 -left-20 h-[380px] w-[380px] rounded-full"
                style={{ background: "radial-gradient(circle, rgba(201,165,92,0.07) 0%, rgba(201,165,92,0) 70%)" }}
            />
            <div className="container-srm relative py-16 lg:py-24">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <span aria-hidden className={`mx-auto ${GOLD_RULE}`} />
                    <h2 className="mt-6 font-display font-bold text-[1.85rem] sm:text-[2.3rem] lg:text-[2.6rem] tracking-[-0.024em] leading-[1.1] text-white text-balance">
                        Speak with SRM.
                    </h2>
                    <p className="mt-6 text-[15px] lg:text-[16.5px] text-white/80 leading-[1.85] max-w-2xl mx-auto">
                        Independent advice tailored to your roof asset. Talk with our
                        team about an assessment, technical report or long-term planning.
                    </p>
                    <div className="mt-10 flex justify-center">
                        <Link
                            to="/contact"
                            data-testid="article-cta-primary"
                            className="group inline-flex items-center justify-center gap-2 rounded-sm bg-srm-gold text-white text-[12px] font-semibold tracking-[0.2em] uppercase px-10 h-[56px] border border-srm-gold shadow-[0_10px_30px_rgba(201,165,92,0.30)] hover:bg-srm-goldDark hover:border-srm-goldDark hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(201,165,92,0.40)] transition-all duration-300"
                        >
                            Speak with the Team
                            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function ArticleDetail() {
    const { slug } = useParams();
    const article = getArticleBySlug(slug);

    usePageMeta({
        title: article
            ? article.seo?.title || `${article.title} | SRM`
            : "Article Not Found | SRM",
        description: article?.seo?.description || article?.summary,
        ogTitle: article?.seo?.ogTitle || article?.title,
        ogDescription: article?.seo?.ogDescription || article?.summary,
        ogImage: article?.heroImage,
        ogType: article ? "article" : undefined,
        path: `/resources/${slug}`,
        breadcrumbs: [
            { name: "Home", url: "/" },
            { name: "Resources", url: "/resources" },
            { name: article?.title || "Article", url: `/resources/${slug}` },
        ],
        articleSchema: article
            ? {
                  headline: article.title,
                  description: article.seo?.description || article.summary,
                  authorName: article.author,
                  authorRole: article.authorRole,
                  datePublished:
                      article.publishedDateISO || article.publishedDate,
                  image: article.heroImage,
              }
            : null,
    });

    if (!article) {
        return <Navigate to="/resources" replace />;
    }

    return (
        <div data-testid="article-page" className="min-h-screen bg-white">
            <Navbar />
            <main id="main">
                <ArticleHero article={article} />
                <ArticleBody article={article} />
                <RelatedArticles currentSlug={article.slug} />
                <ArticleCTA />
            </main>
            <Footer />
        </div>
    );
}
